import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faTag, faArrowLeft, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../utils/Loader';
import api from '../../data/api.json';
import { trackApi } from '../../utils/TrackIp';
import useLocalStorage from '../../utils/useLocalStorage';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/stackoverflow-dark.css';
import Tags from './Tags';
import SEO from '../Seo';
import './../../css/home.css';
import './../../css/blog.css';

function Blog() {
  let { slug } = useParams();
  const [blogContent, setBlogContent] = useState('');
  const [activeBlog, setActiveBlog] = useState({});
  const [blogs, setBlogs] = useLocalStorage('blogsData', []);

  document.title = activeBlog ? `${activeBlog.title} - Nikhil Bhatt` : 'Blog - Nikhil Bhatt';

  const formatDate = (date) => {
    let formattedDate = new Date(date);
    return String(formattedDate).slice(0, 16);
  };
  const formatReadTime = (content) => {
    if (!content) return '1 min read';
    const wordsPerMinute = 200;
    const wordCount = content.split(' ').length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  const showOriginalBlogLink = (link) => {
    if (!link) return null;

    const cleanLink = new URL(link).origin;

    return (
      <div className="original-post-notice">
        <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
        <span>This post was originally published at </span>
        <a href={link} target="_blank" rel="noopener noreferrer" className="original-link">
          {cleanLink}
        </a>
      </div>
    );
  };

  const fetchBlog = (slug) => {
    const url = `${api['latest']}?type=blog&column=9&slug=${slug}`;

    fetch(url, { muteHttpExceptions: true })
      .then((response) => response.json())
      .then((result) => {
        setActiveBlog(result.requestedData);
        setBlogContent(result.requestedData.content);

        if (blogs.length > 0) {
          const updatedData = blogs.map((item) => {
            if (item.slug === slug) {
              return result.requestedData;
            } else {
              return item;
            }
          });

          setBlogs(updatedData);
        }
      })
      .catch((error) => {
        console.error('Error fetching blog:', error);
      });
  };

  useEffect(() => {
    if (blogs.length > 0) {
      const blogData = blogs.find((item) => item.slug === slug);

      if (blogData) {
        setActiveBlog(blogData);
        setBlogContent(blogData.content);
      }
    }

    fetchBlog(slug);
    trackApi(slug);
  }, [slug, blogs]);

  if (!activeBlog || Object.keys(activeBlog).length === 0) {
    return (
      <>
        <SEO page="blogs" />
        <div className="blog-page section-padding">
          <div className="container">
            <Loader />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        page="blogs"
        customTitle={activeBlog.title}
        customDescription={activeBlog.description}
        customImage={activeBlog.featuredImage}
      />
      <div className="blog-page section-padding">
        <div className="container">
          <div className="back-to-blogs mb-2">
            <a href="/blogs" className="btn btn-sm btn-outline-primary">
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Blogs
            </a>
          </div>

          {/* Blog Header */}
          <div className="blog-header">
            <div className="blog-meta-header">
              <span className="blog-category-badge">
                <FontAwesomeIcon icon={faTag} className="me-1" />
                {activeBlog.category || 'Uncategorized'}
              </span>
              <span className="blog-date-badge">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                {formatDate(activeBlog.date)}
              </span>
              <span className="blog-read-time-badge">
                <FontAwesomeIcon icon={faClock} className="me-1" />
                {formatReadTime(activeBlog.content)}
              </span>
            </div>

            {/* Tags */}
            {activeBlog.tags && (
              <div className="blog-tags-section">
                {Tags(activeBlog.tags.split(','))}
              </div>
            )}

            <h1 className="blog-title">{activeBlog.title}</h1>

          </div>

          {/* Blog Content */}
          <div className="blog-content">
            {activeBlog.featuredImage && (
              <div className="blog-featured-image">
                <img
                  src={activeBlog.featuredImage}
                  alt={activeBlog.title}
                  className="featured-image"
                />
              </div>
            )}

            <div className="blog-content-inner">
              {blogContent ? (
                <Markdown rehypePlugins={[rehypeRaw, rehypeHighlight]} >{blogContent}</Markdown>
              ) : (
                <Loader />
              )}
            </div>
          </div>

          {activeBlog.link && showOriginalBlogLink(activeBlog.link)}
        </div>
      </div>
    </>
  );
}

export default Blog;
